import { PRODUCT_CODE, type ProductCode } from '../constants';
import { BUSINESS_PROFILES } from './business';
import { ENGINEERING_PROFILES } from './engineering';
import { PERSONAL_PROFILES } from './personal';
import {
  PROFILE_CATEGORY_LABEL,
  PROFILE_CATEGORY_ORDER,
  type FolderTemplateNode,
  type ProfileCategory,
  type WorkspaceProfile,
} from './types';

export * from './checklists';
export * from './questions';
export * from './reviews';
export * from './types';
export { BUSINESS_PROFILES } from './business';
export { ENGINEERING_PROFILES } from './engineering';
export { PERSONAL_PROFILES } from './personal';

/** Catálogo completo de perfiles de uso. */
export const WORKSPACE_PROFILES: WorkspaceProfile[] = [
  ...PERSONAL_PROFILES,
  ...ENGINEERING_PROFILES,
  ...BUSINESS_PROFILES,
];

const BY_CODE = new Map(WORKSPACE_PROFILES.map((profile) => [profile.code, profile]));

/** Perfil aplicado cuando el usuario no elige ninguno. */
export const DEFAULT_PROFILE_CODE: Record<ProductCode, string> = {
  [PRODUCT_CODE.PERSONAL]: 'PERSONAL_GENERAL',
  [PRODUCT_CODE.BUSINESS]: 'GENERAL_BUSINESS',
};

export function getWorkspaceProfile(code: string | null | undefined): WorkspaceProfile | null {
  if (!code) return null;
  return BY_CODE.get(code) ?? null;
}

export function isValidProfileCode(code: string): boolean {
  return BY_CODE.has(code);
}

/** Perfiles ofrecidos en el formulario de alta para un producto. */
export function listWorkspaceProfiles(productCode?: ProductCode): WorkspaceProfile[] {
  const profiles = productCode
    ? WORKSPACE_PROFILES.filter((profile) => profile.productCode === productCode)
    : WORKSPACE_PROFILES;
  return [...profiles].sort((a, b) => a.sortOrder - b.sortOrder);
}

export interface ProfileGroup {
  category: ProfileCategory;
  label: string;
  profiles: WorkspaceProfile[];
}

/**
 * Perfiles agrupados por categoría, en el orden en que deben mostrarse.
 *
 * El catálogo ya no cabe cómodamente en una sola rejilla: agrupar evita que
 * quien crea la cuenta tenga que leer veinte tarjetas para encontrar la suya.
 * Las categorías sin perfiles no se devuelven.
 */
export function groupWorkspaceProfiles(productCode?: ProductCode): ProfileGroup[] {
  const profiles = listWorkspaceProfiles(productCode);

  return PROFILE_CATEGORY_ORDER.map((category) => ({
    category,
    label: PROFILE_CATEGORY_LABEL[category],
    profiles: profiles.filter((profile) => profile.category === category),
  })).filter((group) => group.profiles.length > 0);
}

/**
 * Resuelve el perfil a aplicar: el elegido si es válido y pertenece al
 * producto contratado; si no, el perfil por defecto del producto.
 */
export function resolveProfile(code: string | null | undefined, productCode: ProductCode) {
  const requested = getWorkspaceProfile(code);
  if (requested && requested.productCode === productCode) return requested;
  return getWorkspaceProfile(DEFAULT_PROFILE_CODE[productCode]) as WorkspaceProfile;
}

export interface FlatFolderTemplateEntry {
  name: string;
  /** Ruta completa separada por `/`, útil como clave estable. */
  path: string;
  /** Ruta del padre, o `null` si es raíz. */
  parentPath: string | null;
  depth: number;
}

/**
 * Aplana el árbol de carpetas del perfil en orden de creación
 * (padres siempre antes que hijos).
 */
export function flattenFolderTemplate(
  nodes: FolderTemplateNode[],
  parentPath: string | null = null,
  depth = 0,
): FlatFolderTemplateEntry[] {
  const entries: FlatFolderTemplateEntry[] = [];
  for (const node of nodes) {
    const path = parentPath ? `${parentPath}/${node.name}` : node.name;
    entries.push({ name: node.name, path, parentPath, depth });
    if (node.children?.length) {
      entries.push(...flattenFolderTemplate(node.children, path, depth + 1));
    }
  }
  return entries;
}

/** Número total de carpetas que crearía un perfil (tope de seguridad en el alta). */
export function countTemplateFolders(profile: WorkspaceProfile): number {
  return flattenFolderTemplate(profile.folderTemplate).length;
}

export const MAX_TEMPLATE_FOLDERS = 100;
